import React, { useState, useEffect, useMemo, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { CreditCard, AlertCircle, Loader2 } from 'lucide-react';
import subscriptionService from '../../services/subscriptionService';
import { useInternetStatus } from '../shared/InternetStatus';

// IMPORTANT:
// Don't initialize/load Stripe while offline, otherwise `stripe.js` fails to load and triggers an
// ugly "Uncaught runtime errors" overlay. We create `stripePromise` only when online.

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#000',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
  hidePostalCode: true,
};

const buildSafeMetadata = (metadata = {}) => {
  const safeEntries = Object.entries(metadata).filter(([, value]) => {
    const valueType = typeof value;
    return valueType !== 'function' && value !== undefined;
  });
  return Object.fromEntries(safeEntries);
};

const PaymentForm = ({ 
  subscriptionId,
  billingInterval = 'monthly',
  amount,
  onSuccess, 
  onError,
  metadata = {}
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [intentContext, setIntentContext] = useState({});
  const [cardholderName, setCardholderName] = useState(metadata?.cardholderName || '');
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Create payment intent when subscription / amount / interval change.
  // Do not depend on `onError`: parent modals often pass a new function each render (e.g. after
  // cardholder name state updates), which would re-fire this effect and spam the API (429).
  useEffect(() => {
    let cancelled = false;
    setClientSecret(null);
    setError(null);

    const createPaymentIntent = async () => {
      try {
        const safeMetadata = buildSafeMetadata(metadata);
        const response = await subscriptionService.createSubscriptionPaymentIntent({
          subscription_id: subscriptionId,
          billing_interval: billingInterval,
          metadata: safeMetadata,
        });

        if (cancelled) return;

        if (response.success && response.data.client_secret) {
          setClientSecret(response.data.client_secret);
          setIntentContext({
            stripe_customer_id: response.data.stripe_customer_id || null,
            stripe_subscription_id: response.data.stripe_subscription_id || null,
          });
        } else {
          const msg = response.message || 'Failed to initialize payment';
          setError(msg);
          onErrorRef.current?.(msg);
        }
      } catch (err) {
        if (cancelled) return;
        const errorMessage = err.response?.data?.message || err.message || 'Failed to create payment intent';
        setError(errorMessage);
        onErrorRef.current?.(errorMessage);
      }
    };

    if (subscriptionId && amount > 0) {
      createPaymentIntent();
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- metadata/onError omitted to avoid intent refetch storms; interval & ids drive refetch
  }, [subscriptionId, amount, billingInterval]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!cardholderName || cardholderName.trim().length < 2) {
      setError('Please enter the cardholder name');
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      // Create payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: cardholderName,
        },
      });

      if (pmError) {
        setError(pmError.message);
        setProcessing(false);
        if (onError) {
          onError(pmError.message);
        }
        return;
      }

      // Confirm payment intent
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethod.id,
        }
      );

      if (confirmError) {
        setError(confirmError.message);
        setProcessing(false);
        if (onError) {
          onError(confirmError.message);
        }
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Confirm payment with backend
        const confirmResponse = await subscriptionService.confirmSubscriptionPayment({
          subscription_id: subscriptionId,
          payment_intent_id: paymentIntent.id,
          payment_method_id: paymentMethod.id,
          cardholder_name: cardholderName,
          billing_interval: billingInterval,
          metadata: {
            ...buildSafeMetadata(metadata),
            stripe_customer_id: intentContext?.stripe_customer_id || undefined,
            stripe_subscription_id: intentContext?.stripe_subscription_id || undefined,
          }
        });

        if (confirmResponse.success) {
          if (onSuccess) {
            onSuccess(confirmResponse.data);
          }
        } else {
          setError(confirmResponse.message || 'Payment confirmation failed');
          setProcessing(false);
          if (onError) {
            onError(confirmResponse.message || 'Payment confirmation failed');
          }
        }
      } else {
        setError('Payment was not successful. Please try again.');
        setProcessing(false);
        if (onError) {
          onError('Payment was not successful. Please try again.');
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during payment';
      setError(errorMessage);
      setProcessing(false);
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-600">Initializing payment...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Cardholder Name */}
      <div>
        <label htmlFor="cardholder-name" className="block text-sm font-medium text-gray-700 mb-1.5">
          Cardholder Name
        </label>
        <input
          id="cardholder-name"
          type="text"
          value={cardholderName}
          onChange={(e) => {
            setCardholderName(e.target.value);
            if (metadata?.onCardholderNameChange) {
              metadata.onCardholderNameChange(e.target.value);
            }
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none text-sm"
          placeholder="John Doe"
          required
          disabled={processing}
        />
      </div>

      {/* Card Element */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Card Details
        </label>
        <div className="px-3 py-3 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-black focus-within:border-black">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {/* Amount Display */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">Total Amount</span>
          <span className="text-lg font-bold text-black">${Number(amount).toFixed(2)}</span>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={processing || !stripe || !cardholderName}
        className="w-full py-3 px-4 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Processing Payment...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            Pay ${Number(amount).toFixed(2)}
          </>
        )}
      </button>

      <p className="text-xs text-gray-500 text-center">
        Your payment is secured by Stripe. We never store your card details.
      </p>
    </form>
  );
};

const StripeSubscriptionPaymentForm = ({ 
  subscriptionId,
  billingInterval = 'monthly',
  amount,
  onSuccess, 
  onError,
  metadata = {}
}) => {
  const { isOnline, isChecking } = useInternetStatus();

  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  const stripePromise = useMemo(() => {
    if (!isOnline || !publishableKey) return null;
    return loadStripe(publishableKey);
  }, [isOnline, publishableKey]);

  if (!publishableKey) {
    return (
      <div className="border-2 border-red-300 rounded-lg p-6 bg-red-50">
        <div className="flex items-center text-red-600 mb-2">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span className="font-semibold">Stripe Configuration Error</span>
        </div>
        <p className="text-sm text-red-600">
          Stripe publishable key is not configured. Please check your .env file.
        </p>
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div className="border-2 border-yellow-200 rounded-lg p-6 bg-yellow-50">
        <div className="flex items-center text-yellow-800 mb-2">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span className="font-semibold">No internet connection</span>
        </div>
        <p className="text-sm text-yellow-900">
          Please reconnect to the internet to complete your payment{isChecking ? '...' : '.'}
        </p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentForm
        subscriptionId={subscriptionId}
        billingInterval={billingInterval}
        amount={amount}
        onSuccess={onSuccess}
        onError={onError}
        metadata={metadata}
      />
    </Elements>
  );
};

export default StripeSubscriptionPaymentForm;

